/**
 * @file Contributor dashboard UI — pages/Dashboard/Contributor/Stages/A3/utils/a3FuelEmbodiedEnergyKwh.ts.
 */

import type { FormDataResponse } from "../../../../../../interfaces/formInterfaces";
import { A1_PETROL_DIESEL_EMBODIED_ENERGY_CONVERSION_FACTOR } from "../../../../../../constants/a1EmbodiedEnergyFactors";
import {
  classifyFuelMixForA1EmbodiedEnergy,
  computeA1EmbodiedEnergyMjFromFuelRow,
  resolveA1EmbodiedEnergyMjUnitSelectValue,
  resolveA1UnitLabelFromValue,
} from "../../../../../../utils/a1FuelEmbodiedEnergy";
import { getFuelMixDropdownLabelForField } from "../../shared/fuelMixReferenceAutofill";
import { discoverA3FuelFieldNames, type A3FuelFieldNameMap } from "./a3FuelMixAutofill";
import {
  processFuelFlatStart,
  totalA3FuelFlatRows,
} from "./a3ProcessFuelLayout";

const FUEL_CODE = "A3_PROCESS_FUEL_DETAILS";

/** Exported const — A3 EMBODIED ENERGY SECTION CODE. */
export const A3_EMBODIED_ENERGY_SECTION_CODE = "A3_EMBODIED_ENERGY_DETAILS";

/** CMS field names for embodied energy value + unit (`_*_A3_EMBODIED_ENERGY_DETAILS_rowN`). */
export function discoverA3EmbodiedEnergyValueAndUnitFields(
  formData: FormDataResponse | null
): { valueField: string; unitField?: string } | null {
  const sec = formData?.formSubSections?.find(
    (s) => s.code === A3_EMBODIED_ENERGY_SECTION_CODE
  );
  if (!sec?.fields?.length) return null;
  const L = (s: string | undefined) => (s ?? "").toLowerCase();
  const valueField = sec.fields.find(
    (f) =>
      f.type !== "UNIT" &&
      L(f.name).includes("embodiedenergy") &&
      !L(f.name).includes("unit") &&
      !L(f.name).includes("process")
  )?.name;
  const unitField = sec.fields.find(
    (f) => f.type === "UNIT" && L(f.name).includes("embodiedenergy")
  )?.name;
  if (!valueField) return null;
  return { valueField, unitField };
}

function parseNum(raw: string | undefined): number {
  const n = Number(String(raw ?? "").replace(/,/g, "").trim());
  return Number.isFinite(n) ? n : NaN;
}

/**
 * Embodied energy (MJ) for one flat A3 fuel row — same rules as A1.
 */
export function computeA3EmbodiedEnergyMjForFuelFlatRow(
  formData: FormDataResponse,
  values: Record<string, string>,
  fuelNames: A3FuelFieldNameMap,
  flatIndex: number,
  unitTokenById?: Record<string, string>
): number | null {
  const mixField = fuelNames.mix;
  if (!mixField) return null;

  const mix = String(
    values[`${mixField}_${FUEL_CODE}_row${flatIndex}`] ?? ""
  ).trim();
  if (!mix) return null;

  const label = getFuelMixDropdownLabelForField(
    formData,
    FUEL_CODE,
    mixField,
    mix
  );
  const cat = classifyFuelMixForA1EmbodiedEnergy(mix, label);
  const qField = fuelNames.quantity ?? "fuelQuantity_A3_01";
  const calField =
    fuelNames.calorific ?? "A3ManufacturingProcessFuelCalorificValue";
  const qtyUnitField = fuelNames.quantityUnit ?? "fuelQuantity_unit_A3_01";
  const densityField = fuelNames.density ?? "fuelDensity_A3_01";
  const densityUnitField = fuelNames.densityUnit ?? "fuelDensity_unit_A3_01";

  const qty = parseNum(values[`${qField}_${FUEL_CODE}_row${flatIndex}`]);
  const cal = parseNum(values[`${calField}_${FUEL_CODE}_row${flatIndex}`]);
  const qtyUnitRaw = values[`${qtyUnitField}_${FUEL_CODE}_row${flatIndex}`] ?? "";
  const density = parseNum(values[`${densityField}_${FUEL_CODE}_row${flatIndex}`]);
  const densityUnitRaw =
    values[`${densityUnitField}_${FUEL_CODE}_row${flatIndex}`] ?? "";

  return computeA1EmbodiedEnergyMjFromFuelRow(cat, qty, cal, {
    quantityUnitValue: qtyUnitRaw,
    quantityUnitLabel: resolveA1UnitLabelFromValue(
      qtyUnitRaw,
      [],
      unitTokenById
    ),
    fuelDensity: Number.isFinite(density) ? density : undefined,
    densityUnitLabel: resolveA1UnitLabelFromValue(
      densityUnitRaw,
      [],
      unitTokenById
    ),
  });
}

/** @deprecated Use computeA3EmbodiedEnergyMjForFuelFlatRow. */
export function computeA3EmbodiedEnergyKwhForFuelFlatRow(
  formData: FormDataResponse,
  values: Record<string, string>,
  fuelNames: A3FuelFieldNameMap,
  flatIndex: number
): number | null {
  const mj = computeA3EmbodiedEnergyMjForFuelFlatRow(
    formData,
    values,
    fuelNames,
    flatIndex
  );
  return mj != null ? Math.round((mj / 3.6) * 1000) / 1000 : null;
}

/** Exported function — build Default A3Embodied Energy Unit Updates. */
export function buildDefaultA3EmbodiedEnergyUnitUpdates(
  formData: FormDataResponse | null,
  values: Record<string, string>,
  processRowCount: number,
  options?: { mjUnitId?: string | null }
): Record<string, string> {
  if (!formData || processRowCount <= 0) return {};

  const energyFields = discoverA3EmbodiedEnergyValueAndUnitFields(formData);
  if (!energyFields?.unitField) return {};

  const mjUnit = options?.mjUnitId?.trim() || "MJ";
  const updates: Record<string, string> = {};

  for (let p = 0; p < processRowCount; p++) {
    const uKey = `${energyFields.unitField}_${A3_EMBODIED_ENERGY_SECTION_CODE}_row${p}`;
    const v = String(values[uKey] ?? "").trim();
    if (/^[0-9]+$/.test(v)) continue;
    if (v === "kWh") continue;
    if (v === "" || v === "MJ") {
      if (v !== mjUnit) updates[uKey] = mjUnit;
    }
  }

  return updates;
}

/**
 * Auto-fill `A3_EMBODIED_ENERGY_DETAILS` value (MJ unit) per sub-process row.
 */
export function buildA3EmbodiedEnergyDetailsValueUpdates(
  formData: FormDataResponse | null,
  values: Record<string, string>,
  processRowCount: number,
  fuelsPerProcess: number[],
  options?: {
    mjUnitId?: string | null;
    unitTokenById?: Record<string, string>;
  }
): Record<string, string> {
  if (!formData || processRowCount <= 0) return {};

  const energyFields = discoverA3EmbodiedEnergyValueAndUnitFields(formData);
  if (!energyFields?.valueField) return {};

  const fuelNames = discoverA3FuelFieldNames(formData);
  if (!fuelNames.mix) return {};

  const mjId = options?.mjUnitId?.trim() || "MJ";
  const unitTokenById = options?.unitTokenById ?? {};

  const counts =
    fuelsPerProcess.length >= processRowCount
      ? fuelsPerProcess
      : [
          ...fuelsPerProcess,
          ...Array.from(
            { length: Math.max(0, processRowCount - fuelsPerProcess.length) },
            () => 1
          ),
        ];

  const updates: Record<string, string> = {};
  for (let p = 0; p < processRowCount; p++) {
    const start = processFuelFlatStart(p, counts);
    const nFuels = Math.max(1, counts[p] ?? 1);
    let sum = 0;
    for (let j = 0; j < nFuels; j++) {
      const flat = start + j;
      const mj = computeA3EmbodiedEnergyMjForFuelFlatRow(
        formData,
        values,
        fuelNames,
        flat,
        unitTokenById
      );
      if (mj != null && Number.isFinite(mj)) sum += mj;
    }
    const rounded = Math.round(sum * 1000) / 1000;
    const valueKey = `${energyFields.valueField}_${A3_EMBODIED_ENERGY_SECTION_CODE}_row${p}`;
    updates[valueKey] = rounded > 0 ? String(rounded) : "";

    if (energyFields.unitField) {
      const unitKey = `${energyFields.unitField}_${A3_EMBODIED_ENERGY_SECTION_CODE}_row${p}`;
      if (rounded > 0) {
        updates[unitKey] = mjId;
      } else {
        updates[unitKey] = "";
      }
    }
  }

  return updates;
}

/**
 * Sum of embodied energy (MJ) across all A3 manufacturing fuel rows.
 */
export function computeA3CumulativeFuelEmbodiedEnergyMj(params: {
  formData: FormDataResponse | null;
  values: Record<string, string>;
  fuelsPerProcess: number[];
  fuelNames: A3FuelFieldNameMap;
  unitTokenById?: Record<string, string>;
}): number {
  const { formData, values, fuelsPerProcess, fuelNames, unitTokenById } =
    params;

  if (!formData || !fuelNames.mix) return 0;

  const n = totalA3FuelFlatRows(fuelsPerProcess);
  let sum = 0;
  for (let flat = 0; flat < n; flat++) {
    const mj = computeA3EmbodiedEnergyMjForFuelFlatRow(
      formData,
      values,
      fuelNames,
      flat,
      unitTokenById
    );
    if (mj != null && Number.isFinite(mj)) sum += mj;
  }
  return Math.round(sum * 1000) / 1000;
}

/** @deprecated Use computeA3CumulativeFuelEmbodiedEnergyMj. */
export function computeA3CumulativeFuelEmbodiedEnergyKwh(params: {
  formData: FormDataResponse | null;
  values: Record<string, string>;
  fuelsPerProcess: number[];
  fuelNames: A3FuelFieldNameMap;
  petrolDieselConversionFactor?: number;
}): number {
  const mj = computeA3CumulativeFuelEmbodiedEnergyMj(params);
  return mj > 0 ? Math.round((mj / 3.6) * 1000) / 1000 : 0;
}

export { resolveA1EmbodiedEnergyMjUnitSelectValue as resolveA3EmbodiedEnergyMjUnitSelectValue };

/** Read optional admin/backend override for petrol/diesel MJ→kWh factor (default 0.278). */
export function pickPetrolDieselEmbodiedEnergyConversionFromSubmission(
  submission: unknown
): number {
  const s = submission as Record<string, unknown> | null | undefined;
  if (!s) return A1_PETROL_DIESEL_EMBODIED_ENERGY_CONVERSION_FACTOR;

  const candidates: unknown[] = [
    s.petrolDieselEmbodiedEnergyConversionFactor,
    s.petrolDieselConversionFactor,
    s.embodiedEnergyConversionFactorPetrolDiesel,
    (s.settings as Record<string, unknown> | undefined)
      ?.petrolDieselEmbodiedEnergyConversionFactor,
    (s.background as Record<string, unknown> | undefined)
      ?.petrolDieselEmbodiedEnergyConversionFactor,
  ];

  for (const c of candidates) {
    const n = Number(c);
    if (Number.isFinite(n) && n > 0) return n;
  }
  return A1_PETROL_DIESEL_EMBODIED_ENERGY_CONVERSION_FACTOR;
}
